import type { CSSProperties } from 'react';
import dagre from 'dagre';
import { Position, type Edge, type Node } from '@xyflow/react';
import {
  WORKFLOW_FLOW_NODE_TYPE,
  WORKFLOW_FLOW_TRIGGER_TYPE,
  workflowTriggerViewFromSnapshot,
  type WorkflowFlowNodeData,
  type WorkflowFlowTriggerData,
} from './components';
import type { WorkflowGraphApiData } from './workflow-graph.types';
import type { WorkflowNodeViewData } from './components/node';
import {
  WORKFLOW_FLOW_NODE_HEIGHT,
  WORKFLOW_FLOW_NODE_WIDTH,
  WORKFLOW_FLOW_TRIGGER_HEIGHT,
  WORKFLOW_FLOW_TRIGGER_WIDTH,
} from './workflow-flow-layout.constants';
import { SYNTHETIC_TRIGGER_EDGE_PREFIX } from './workflow-flow-node-ids';
import {
  WORKFLOW_DISABLED_EDGE_MARKER,
  WORKFLOW_DISABLED_EDGE_STYLE,
  WORKFLOW_EDGE_TYPE,
} from './workflow-edge-style';
import { findEntryNodeIdsFromEdges } from './workflow-entry-run-input';
import {
  listForEachFanInEdgeKeys,
  listHiddenForEachInteriorNodeIds,
  withForEachBodyDisplayFields,
} from './components/node/edit-strategies/for-each-child-node';

export {
  SYNTHETIC_TRIGGER_EDGE_PREFIX,
  isSyntheticWorkflowEdgeId,
  isWorkflowTriggerFlowNodeId,
} from './workflow-flow-node-ids';

type WorkflowFlowNode = Node<WorkflowFlowTriggerData | WorkflowFlowNodeData>;

const FOR_EACH_FAN_IN_EDGE_LABEL = 'Fan in';

const FOR_EACH_FAN_IN_LABEL_STYLE: CSSProperties = {
  fill: 'var(--muted-foreground)',
  fontSize: 10,
  fontWeight: 500,
};

const FOR_EACH_FAN_IN_LABEL_BG_STYLE: CSSProperties = {
  fill: 'var(--background)',
};

/**
 * The view-model the editor passes a node into its config form with. The one
 * place this shape is built from a graph node, so a card click (via the flow
 * node data below) and opening a node by id off-canvas (the for-each loop body —
 * see `openNodeConfigById`) can't drift apart.
 */
export function workflowNodeViewDataFromGraphNode(
  node: WorkflowGraphApiData['nodes'][number]
): WorkflowNodeViewData {
  return {
    id: node.id,
    type: node.type,
    data: node.data,
    inputShape: node.inputShape,
    defaultOutput: node.defaultOutput,
  };
}

/**
 * Whether a trigger will actually fire this workflow.
 *
 * Dispatch requires the binding *and* the trigger row (see
 * `workflow-resource-type.strategy.ts`, which joins on both), so the canvas has
 * to agree: this is the one place the rule is stated, and both the card's
 * dimming and its connector's styling read it. Splitting it in two is what let
 * a live binding on a disabled row draw a faded card with a solid edge.
 */
function isTriggerRunning(
  bindingEnabled: boolean,
  rowEnabled: boolean | undefined
): boolean {
  return bindingEnabled && rowEnabled !== false;
}

function findEntryNodeIds(
  nodeIds: readonly string[],
  edges: WorkflowGraphApiData['edges']
): string[] {
  return findEntryNodeIdsFromEdges(nodeIds, edges);
}

function workflowFlowNodeDimensions(node: WorkflowFlowNode): {
  width: number;
  height: number;
} {
  if (node.type === WORKFLOW_FLOW_TRIGGER_TYPE) {
    return {
      width: WORKFLOW_FLOW_TRIGGER_WIDTH,
      height: WORKFLOW_FLOW_TRIGGER_HEIGHT,
    };
  }
  return {
    width: WORKFLOW_FLOW_NODE_WIDTH,
    height: WORKFLOW_FLOW_NODE_HEIGHT,
  };
}

/**
 * Top-to-bottom dagre layout shared by the editor canvas and the read-only run
 * graph. `getDimensions` supplies per-node size so callers can mix trigger /
 * node / run-node sizes.
 */
export function layoutFlowGraph<N extends Node>(
  nodes: N[],
  edges: Edge[],
  getDimensions: (node: N) => { width: number; height: number }
): { nodes: N[]; edges: Edge[] } {
  if (nodes.length === 0) {
    return { nodes, edges };
  }

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB',
    nodesep: 72,
    ranksep: 96,
    marginx: 24,
    marginy: 24,
  });

  for (const node of nodes) {
    const { width, height } = getDimensions(node);
    dagreGraph.setNode(node.id, { width, height });
  }

  for (const edge of edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const { width, height } = getDimensions(node);
    const nodeWithPosition = dagreGraph.node(node.id) as {
      x: number;
      y: number;
    };
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });

  const layoutedEdges = edges.map((edge) => ({
    ...edge,
    type: WORKFLOW_EDGE_TYPE,
  }));

  return { nodes: layoutedNodes, edges: layoutedEdges };
}

function layoutWorkflowFlowElements(
  nodes: WorkflowFlowNode[],
  edges: Edge[]
): { nodes: WorkflowFlowNode[]; edges: Edge[] } {
  return layoutFlowGraph(nodes, edges, workflowFlowNodeDimensions);
}

/**
 * Converts a persisted workflow graph into React Flow nodes and edges.
 *
 * Persisted `workflow_edges` only connect `workflow_nodes`. Triggers live in
 * separate tables, so we synthesize trigger → entry-node edges for the canvas
 * (scheduled-agent creates often have zero rows in `workflow_edges`).
 */
export function workflowGraphToFlow(graph: WorkflowGraphApiData): {
  nodes: Node<WorkflowFlowTriggerData | WorkflowFlowNodeData>[];
  edges: Edge[];
} {
  // Derive for-each body badge fields from the live body node (save only keeps
  // childNodeId).
  const displayGraph = withForEachBodyDisplayFields(graph);

  const triggerNodes: Node<WorkflowFlowTriggerData>[] =
    displayGraph.triggers.map((snapshot) => {
      const binding =
        displayGraph.triggerBindings.find(
          (row) => row.triggerId === snapshot.trigger.id && row.enabled === true
        ) ??
        displayGraph.triggerBindings.find(
          (row) => row.triggerId === snapshot.trigger.id
        );
      return {
        id: `trigger:${snapshot.trigger.id}`,
        type: WORKFLOW_FLOW_TRIGGER_TYPE,
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          trigger: workflowTriggerViewFromSnapshot(snapshot),
          ...(binding !== undefined && {
            bindingId: binding.id,
            running: isTriggerRunning(
              binding.enabled,
              snapshot.trigger.enabled
            ),
          }),
        },
      };
    });

  const entryNodeIds = findEntryNodeIds(
    displayGraph.nodes.map((node) => node.id),
    displayGraph.edges
  );

  const hiddenNodeIds = listHiddenForEachInteriorNodeIds(
    displayGraph.nodes,
    displayGraph.edges
  );

  const workflowNodes: Node<WorkflowFlowNodeData>[] = displayGraph.nodes
    .filter((node) => !hiddenNodeIds.has(node.id))
    .map((node) => {
      const viewData = workflowNodeViewDataFromGraphNode(node);
      return {
        id: node.id,
        type: WORKFLOW_FLOW_NODE_TYPE,
        position: { x: 0, y: 0 },
        data: { node: viewData },
      };
    });

  // Visible for-each outs are always fan-in (loop body is hidden on the canvas).
  const fanInEdgeKeys = listForEachFanInEdgeKeys(
    displayGraph.nodes,
    displayGraph.edges
  );
  const flowEdges: Edge[] = displayGraph.edges
    .filter(
      (edge) =>
        !hiddenNodeIds.has(edge.fromNodeId) && !hiddenNodeIds.has(edge.toNodeId)
    )
    .map((edge) => {
      const isFanIn = fanInEdgeKeys.has(`${edge.fromNodeId}:${edge.toNodeId}`);
      return {
        id: edge.id,
        source: edge.fromNodeId,
        target: edge.toNodeId,
        type: WORKFLOW_EDGE_TYPE,
        ...(isFanIn
          ? {
              label: FOR_EACH_FAN_IN_EDGE_LABEL,
              labelStyle: FOR_EACH_FAN_IN_LABEL_STYLE,
              labelBgStyle: FOR_EACH_FAN_IN_LABEL_BG_STYLE,
              labelBgPadding: [2, 6] as [number, number],
              labelBgBorderRadius: 4,
            }
          : {}),
      };
    });

  // Disabled bindings still get their connector, drawn muted and dashed rather
  // than dropped. A trigger switched off is still wired to this workflow — it
  // is paused, not detached — and a card floating with no line into the graph
  // reads as broken instead.
  const rowEnabledByTriggerId = new Map(
    displayGraph.triggers.map((snapshot) => [
      snapshot.trigger.id,
      snapshot.trigger.enabled,
    ])
  );
  for (const binding of displayGraph.triggerBindings) {
    // A binding whose trigger isn't in the graph has no card to draw from.
    if (rowEnabledByTriggerId.has(binding.triggerId) === false) {
      continue;
    }
    const running = isTriggerRunning(
      binding.enabled,
      rowEnabledByTriggerId.get(binding.triggerId)
    );
    for (const entryId of entryNodeIds) {
      flowEdges.push({
        id: `${SYNTHETIC_TRIGGER_EDGE_PREFIX}${binding.id}:${entryId}`,
        source: `trigger:${binding.triggerId}`,
        target: entryId,
        type: WORKFLOW_EDGE_TYPE,
        reconnectable: false,
        ...(running === false && {
          style: WORKFLOW_DISABLED_EDGE_STYLE,
          markerEnd: WORKFLOW_DISABLED_EDGE_MARKER,
        }),
      });
    }
  }

  return layoutWorkflowFlowElements(
    [...triggerNodes, ...workflowNodes],
    flowEdges
  );
}

export function emptyWorkflowFlowGraph(): {
  nodes: Node<WorkflowFlowTriggerData | WorkflowFlowNodeData>[];
  edges: Edge[];
} {
  return { nodes: [], edges: [] };
}
